import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import CasinoSidebar from '@/components/CasinoSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trophy, ChevronRight, Share2, ArrowRight, Check, AlertTriangle, ExternalLink, PartyPopper, DollarSign } from 'lucide-react';
import { createShareCard, copyShareLink } from '@/lib/share';

export default function WinCelebration() {
  const navigate = useNavigate();
  const [winAmount, setWinAmount] = useState('');
  const [gameType, setGameType] = useState('Slots');
  const [casinoName, setCasinoName] = useState('');
  const [sessionsToWin, setSessionsToWin] = useState('1');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!winAmount || parseFloat(winAmount) <= 0) { toast.error('Enter a valid win amount'); return; }
    setLoading(true);
    try {
      const res = await api.post('/tools/win-celebration', {
        win_amount: parseFloat(winAmount), game_type: gameType,
        casino_name: casinoName, sessions_to_win: parseInt(sessionsToWin) || 1,
      });
      setResult(res.data);
    } catch (err) { toast.error('Failed to generate celebration'); }
    finally { setLoading(false); }
  };

  const handleShare = async () => {
    if (result) {
      const card = await createShareCard('win_celebration', { win_amount: result.win_amount, game_type: result.game_type, casino_name: result.casino_name });
      if (card) copyShareLink(card.share_url);
      api.post(`/tools/win-celebration/${result.celebration_id}/share`).catch(() => {});
    }
  };

  const handleLogToStreak = () => {
    navigate('/viral-tools/betting-streak-tracker');
    toast.info('Log your session in the Streak Tracker');
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="win-celebration-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link>
        <ChevronRight className="h-3.5 w-3.5" /><span className="text-[#a0a8b8]">Emotional Wellness</span>
        <ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Win Celebration</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#f59e0b]/15 flex items-center justify-center shrink-0"><Trophy className="h-6 w-6 text-[#f59e0b]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Win Celebration Generator</h1><p className="text-[#a0a8b8] text-sm mt-1">Celebrate your wins smartly - channel excitement into good decisions</p></div>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          {!result ? (
            <div className="max-w-lg">
              <div className="ciq-card">
                <form onSubmit={handleGenerate} className="space-y-5">
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Win Amount</label>
                    <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={winAmount} onChange={e => setWinAmount(e.target.value)} required min="1" step="0.01" placeholder="150"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="win-amount-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Game Played</label>
                    <Select value={gameType} onValueChange={setGameType}>
                      <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="win-game-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#252b3b] border-white/10">
                        {['Slots', 'Blackjack', 'Roulette', 'Baccarat', 'Video Poker', 'Live Casino', 'Other'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Casino Name</label>
                    <input type="text" value={casinoName} onChange={e => setCasinoName(e.target.value)} placeholder="e.g. BitStarz"
                      className="w-full h-11 px-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="win-casino-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">How many sessions did it take?</label>
                    <input type="number" value={sessionsToWin} onChange={e => setSessionsToWin(e.target.value)} min="1" placeholder="1"
                      className="w-full h-11 px-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="win-sessions-input" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#f59e0b] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="win-generate-btn">
                    {loading ? 'Generating...' : 'Generate Victory Card'}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="animate-slide-up space-y-5 max-w-lg">
              {/* Victory Card */}
              <div className="ciq-card border-[#f59e0b]/30 bg-gradient-to-br from-[#f59e0b]/10 to-[#f97316]/5 text-center" data-testid="victory-card">
                <PartyPopper className="h-10 w-10 text-[#f59e0b] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white mb-1">{result.headline}</p>
                <p className="text-[#a0a8b8] text-sm mb-3">{result.subtext}</p>
                <div className="p-3 rounded-lg bg-[#1a1f2e]/80 mb-3">
                  <p className="text-sm text-[#f59e0b] font-medium">{result.fun_stat}</p>
                </div>
                <p className="text-xs text-[#64748b] italic">Smart move: Cash out 50% and play with house money</p>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-[10px] text-[#64748b]">Generated by CasinoIQ</p>
                </div>
              </div>

              {/* Smart Recommendations */}
              <div className="ciq-card">
                <h3 className="text-white font-semibold text-sm mb-3">Smart Money Management</h3>
                <div className="space-y-2.5">
                  {result.recommendations.map((rec, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${rec.type === 'warning' ? 'bg-[#f59e0b]/5 border border-[#f59e0b]/15' : 'bg-[#10b981]/5 border border-[#10b981]/15'}`} data-testid={`rec-${i}`}>
                      {rec.type === 'warning' ? <AlertTriangle className="h-4 w-4 text-[#f59e0b] shrink-0 mt-0.5" /> : <Check className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" />}
                      <p className="text-sm text-[#a0a8b8]">{rec.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button onClick={handleShare} className="h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 flex items-center justify-center gap-2" data-testid="win-share-btn">
                  <Share2 className="h-3.5 w-3.5" /> Share My Win
                </button>
                <button onClick={handleLogToStreak} className="h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 flex items-center justify-center gap-2" data-testid="win-log-streak-btn">
                  <ArrowRight className="h-3.5 w-3.5" /> Log to Tracker
                </button>
                <Link to="/play-smart/withdrawal-predictor" className="h-10 rounded-lg bg-[#4d7cfe] text-white text-sm font-medium flex items-center justify-center gap-2 btn-lift" data-testid="win-withdrawal-btn">
                  <DollarSign className="h-3.5 w-3.5" /> Withdraw Tips
                </Link>
              </div>

              <button onClick={() => setResult(null)} className="w-full text-center text-xs text-[#4d7cfe] hover:underline">Generate another card</button>
            </div>
          )}
        </div>
        <CasinoSidebar sourcePage="win-celebration" />
      </div>
    </div>
  );
}
